import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banknote, CheckCircle, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CompensationReportView, PayrollEntriesResultView } from '@/models/tournament';
import { generatePayrollEntries } from '@/api/TournamentManagement/workPointApi';
import { useCurrency } from '@/hooks/useCurrency';

interface PayrollGenerationSectionProps {
  reportData: CompensationReportView;
  startDate: string;
  endDate: string;
  tournamentId?: string;
  refereeUserId?: string;
}

const PayrollGenerationSection: React.FC<PayrollGenerationSectionProps> = ({
  reportData,
  startDate,
  endDate,
  tournamentId,
  refereeUserId,
}) => {
  const { t } = useTranslation('tournament');
  const { format: formatCurrency } = useCurrency();
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PayrollEntriesResultView | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!reportData.lines || reportData.lines.length === 0) {
    return null;
  }

  const refereeCount = reportData.totalCount ?? reportData.lines.length;
  const totalAmount = reportData.totalCurrencyAmount;

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await generatePayrollEntries({
        startDate,
        endDate,
        tournamentId: tournamentId ? Number(tournamentId) : undefined,
        refereeUserId: refereeUserId || undefined,
      });
      if (res.errors.length > 0) {
        setError(res.errors[0]?.description || t('Failed to generate payroll entries'));
      } else {
        setResult(res.payload);
      }
    } catch {
      setError(t('Failed to generate payroll entries'));
    } finally {
      setGenerating(false);
      setShowConfirm(false);
    }
  };

  if (result) {
    if (result.entriesCreated === 0) {
      return (
        <div
          className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6"
          data-testid="payroll-no-new-entries"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/40 rounded-full flex items-center justify-center">
              <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-100">
                {t('No new entries created')}
              </h3>
            </div>
          </div>
          <p className="text-sm text-amber-800 dark:text-amber-300 mb-4">
            {t('All referees in this period have already been processed. Payroll entries were previously generated for this date range.')}
          </p>
          <button
            onClick={() => setResult(null)}
            className="text-sm font-medium text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 transition-colors"
          >
            {t('Try a different date range')}
          </button>
        </div>
      );
    }

    return (
      <div
        className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-6"
        data-testid="payroll-success"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/40 rounded-full flex items-center justify-center">
            <CheckCircle size={20} className="text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-emerald-900 dark:text-emerald-100">
              {t('Payroll entries generated successfully')}
            </h3>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-emerald-100 dark:border-emerald-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('Entries Created')}</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{result.entriesCreated}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-emerald-100 dark:border-emerald-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('Referees Processed')}</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{result.refereesProcessed}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-emerald-100 dark:border-emerald-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('Total Amount')}</p>
            <p className="text-xl font-bold text-emerald-700 dark:text-emerald-400">{formatCurrency(result.totalAmount)}</p>
          </div>
        </div>
        <Link
          to="/payroll/table"
          className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 transition-colors"
        >
          {t('View in Payroll')}
          <ExternalLink size={14} />
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="payroll-generation-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-sm">
          <Banknote size={20} className="text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('Generate Payroll Entries')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('{{count}} referees, {{amount}} total amount', {
              count: refereeCount,
              amount: formatCurrency(totalAmount),
            })}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-600 dark:text-red-400 shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {!showConfirm ? (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <AlertTriangle size={12} />
            {t('Previously processed referees for this period will be skipped automatically.')}
          </p>
          <button
            onClick={() => setShowConfirm(true)}
            disabled={generating}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-emerald-500/20"
            data-testid="generate-payroll-btn"
          >
            <Banknote size={18} />
            {t('Generate Payroll Entries')}
          </button>
        </div>
      ) : (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4" data-testid="payroll-confirm-dialog">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              {t('payroll_confirm_warning', {
                defaultValue: 'This will create variable pay entries in the Payroll module for {{count}} referees totaling {{amount}}. This action cannot be undone.',
                count: refereeCount,
                amount: formatCurrency(totalAmount),
              })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="confirm-generate-payroll-btn"
            >
              {generating ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {t('Generating...')}
                </>
              ) : (
                t('Confirm')
              )}
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              disabled={generating}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              data-testid="cancel-payroll-btn"
            >
              {t('Cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollGenerationSection;
